import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useVideoGrid } from "../home/hooks/useVideoGrid";

export type SingleVideoProps = {
  moodImages: [any];
  relevantMoods?: Record<number, number>;
  videosReady: any;
};

const SingleVideo: React.FC<SingleVideoProps> = ({
  moodImages,
  relevantMoods,
  videosReady,
}) => {
  const { t } = useTranslation();
  const {
    videoRefs,
    loadedVideos,
    moodToPathMap,
    placeholdersPreloaded,
    handleVideoLoaded,
    handleVideoError,
  } = useVideoGrid({
    moodImages,
    videosReady,
  });

  const [loadedPlaceholders, setLoadedPlaceholders] = useState(() =>
    placeholdersPreloaded ? new Set([0, 1, 2, 3]) : new Set()
  );

  const getDurationLabel = (duration: number) => {
    if (duration === 1) return t(`homepage.1-3`);
    if (duration === 2) return t(`homepage.3-6`);
    if (duration === 4) return t(`homepage.entireDay`);
    return t(`homepage.entireDay`);
  };

  const dominantMoodIndex = useMemo(() => {
    if (!relevantMoods || Object.keys(relevantMoods).length === 0) return 0;
    return Object.entries(relevantMoods).reduce(
      (best, [idx, duration]) =>
        duration > (relevantMoods[best] || 0) ? Number(idx) : best,
      0
    );
  }, [relevantMoods]);

  const first4 = [moodImages[dominantMoodIndex] ?? moodImages[0]].filter(
    Boolean
  );

  const pathsReady = first4.every((m) => {
    const pd = moodToPathMap.get(m.enLabel);
    return Boolean(pd?.placeholder || m.src);
  });

  if (!pathsReady) {
    return (
      <>
        {first4.map((_, i) => (
          <div key={i} className="mood-item">
            <div className="mood-item-inner mood-skeleton" />
            <p className="mood-text">&nbsp;</p>
            <div className="mood-duration-div-outer">
              <div style={{ visibility: "hidden" }} />
            </div>
          </div>
        ))}
      </>
    );
  }

  return (
    <div
      style={{ paddingTop: "16px", paddingLeft: "16px", paddingRight: "16px" }}
    >
      <div className={`mood-selection-container`} style={{ display: "block" }}>
        {first4.map(({ label, enLabel, src }, feelingName) => {
          const pathData = moodToPathMap.get(enLabel);
          const videoSrc = pathData?.video || "";
          const placeholderSrc = pathData?.placeholder || src || "";

          return (
            <div key={feelingName} className="mood-item">
              <div className="mood-item-inner">
                {placeholderSrc && (
                  <>
                    <img
                      src={placeholderSrc}
                      alt={label}
                      className="mood-image"
                      loading="eager"
                      fetchPriority="high"
                      decoding="async"
                      onLoad={() => {
                        setLoadedPlaceholders(
                          (prev) => new Set([...prev, feelingName])
                        );
                      }}
                      style={{
                        opacity: loadedVideos.has(feelingName) ? 0 : 1,
                        visibility: loadedVideos.has(feelingName)
                          ? "hidden"
                          : "visible",
                        transition: loadedVideos.has(feelingName)
                          ? "opacity 0.2s ease-out, visibility 0s linear 0.2s"
                          : "opacity 0.2s ease-in",
                        position: "relative",
                        maxHeight: "240px",
                        pointerEvents: loadedVideos.has(feelingName)
                          ? "none"
                          : "auto",
                        willChange: loadedVideos.has(feelingName)
                          ? "opacity"
                          : "auto",
                      }}
                    />
                  </>
                )}

                {videoSrc && (
                  <video
                    ref={(el) => {
                      videoRefs.current[feelingName] = el;
                    }}
                    src={videoSrc}
                    className="mood-video"
                    preload="auto"
                    loop
                    muted
                    playsInline
                    autoPlay={videosReady}
                    onLoadedData={() => handleVideoLoaded(feelingName)}
                    onCanPlayThrough={() => handleVideoLoaded(feelingName)}
                    onError={() => handleVideoError(feelingName)}
                    onMouseEnter={async () => {
                      const video = videoRefs.current[feelingName];
                      if (video) {
                        try {
                          await video.play();
                        } catch (error) {}
                      }
                    }}
                    style={{
                      opacity: loadedVideos.has(feelingName) ? 1 : 0,
                      visibility: loadedVideos.has(feelingName)
                        ? "visible"
                        : "hidden",
                      transition: loadedVideos.has(feelingName)
                        ? "opacity 0.2s ease-in"
                        : "opacity 0s",
                      width: "100%",
                      height: "auto",
                      pointerEvents: loadedVideos.has(feelingName)
                        ? "auto"
                        : "none",
                      willChange: loadedVideos.has(feelingName)
                        ? "opacity"
                        : "auto",
                    }}
                  />
                )}
              </div>
              <p className="mood-text">{label}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SingleVideo;
